import { ThemeEvents } from '@theme/events';

/** Pointer zoom extends the existing dialog without changing Horizon's core JS. */
class CustomDetailZoom extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.button = this.querySelector('.custom-detail-zoom__toggle');
    this.points = new Map();
    this.scale = 1;
    this.x = this.y = 50;
    this.pinned = false;
    this.fine = matchMedia('(hover: hover) and (pointer: fine)');
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !this.fine.matches || this.dataset.hover === 'false') return;
      this.loadDetail();
      this.scale = Number(this.dataset.scale) || 2;
      this.origin(event);
    }, options);
    this.addEventListener('pointerleave', () => { if (!this.pinned && !this.points.size) this.reset(); }, options);
    this.addEventListener('pointerdown', (event) => this.down(event), options);
    this.addEventListener('pointermove', (event) => this.move(event), options);
    this.addEventListener('pointerup', (event) => this.up(event), options);
    this.addEventListener('pointercancel', () => { this.points.clear(); this.dragged = true; }, options);
    this.addEventListener('click', (event) => {
      event.stopPropagation(); // The native image backdrop normally closes the dialog.
      if (this.dragged) { this.dragged = false; return; }
      if (event.target.closest('button') || !this.fine.matches) {
        this.loadDetail();
        this.pinned = !this.pinned;
        this.scale = this.pinned ? Number(this.dataset.scale) || 2 : 1;
        this.x = this.y = 50;
        this.render();
      }
    }, options);
    this.dialog = this.closest('dialog');
    this.dialog?.addEventListener('toggle', () => { if (!this.dialog.open) this.reset(); }, options);
    this.closest('zoom-dialog')?.addEventListener(ThemeEvents.zoomMediaSelected, () => this.reset(), options);
    this.render();
  }

  origin(event) {
    const rect = this.querySelector('img')?.getBoundingClientRect();
    // Use the stable viewport rather than the transformed image bounds.
    const viewport = this.getBoundingClientRect();
    if (!rect || !viewport.width || !viewport.height) return;
    this.x = Math.max(0, Math.min(100, (event.clientX - viewport.left) / viewport.width * 100));
    this.y = Math.max(0, Math.min(100, (event.clientY - viewport.top) / viewport.height * 100));
    this.render();
  }

  loadDetail() {
    this.closest('zoom-dialog')?.loadHighResolutionImage?.(this.closest('.product-media-container'));
  }

  down(event) {
    if (event.target.closest('button')) return;
    this.dragged = false;
    this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.start = { x: event.clientX, y: event.clientY, originX: this.x, originY: this.y };
    this.setPointerCapture(event.pointerId);
    if (this.points.size === 2) {
      this.pinchDistance = this.distance();
      this.pinchScale = this.scale;
    }
  }

  distance() {
    const [a, b] = [...this.points.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  move(event) {
    if (this.points.has(event.pointerId)) {
      this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.points.size === 2 && this.pinchDistance) {
        this.scale = Math.max(1, Math.min(4, this.pinchScale * this.distance() / this.pinchDistance));
        this.pinned = this.scale > 1;
        this.dragged = true;
        this.render();
      } else if (this.scale > 1 && this.start) {
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        if (Math.hypot(dx, dy) < 5) return;
        this.dragged = true;
        const rect = this.getBoundingClientRect();
        this.x = Math.max(0, Math.min(100, this.start.originX - dx / rect.width * 100));
        this.y = Math.max(0, Math.min(100, this.start.originY - dy / rect.height * 100));
        this.render();
      }
    } else if (this.fine.matches && event.pointerType === 'mouse' && this.scale > 1) this.origin(event);
  }

  up(event) {
    this.points.delete(event.pointerId);
    const remaining = [...this.points.values()][0];
    this.start = remaining ? { x: remaining.x, y: remaining.y, originX: this.x, originY: this.y } : null;
  }

  render() {
    this.style.setProperty('--detail-scale', this.scale);
    this.style.setProperty('--detail-x', `${this.x}%`);
    this.style.setProperty('--detail-y', `${this.y}%`);
    this.toggleAttribute('data-enlarged', this.scale > 1);
    this.button.setAttribute('aria-pressed', String(this.pinned));
    const label = this.pinned ? this.button.dataset.labelOut : this.button.dataset.labelIn;
    this.button.setAttribute('aria-label', label);
    this.button.querySelector('[data-zoom-label]').textContent = label;
  }

  reset() {
    this.scale = 1;
    this.x = this.y = 50;
    this.pinned = false;
    this.points.clear();
    this.render();
  }

  disconnectedCallback() { this.controller?.abort(); this.points.clear(); }
}
if (!customElements.get('custom-detail-zoom')) customElements.define('custom-detail-zoom', CustomDetailZoom);
